/**
 * Client-side email masking utility.
 *
 * Mirror of maskEmail in supabase/functions/validate-beta-invite/handler.ts
 * (handler.ts line 89 — `export function maskEmail`). Keep in sync if the
 * server algorithm changes.
 *
 * Difference from server: this version lowercases the input before masking
 * so client-computed masks can be compared case-insensitively with the
 * server-returned emailHint. The server doesn't lowercase, but masksMatch
 * performs a case-insensitive comparison to bridge the gap.
 */

/**
 * Mask an email address to a hint suitable for UI comparison with the
 * server-returned emailHint from validate-beta-invite.
 *
 * Format: first character of local part (lowercase) + "***@" + domain (lowercase).
 * Examples:
 *   "tyler@example.com"  -> "t***@example.com"
 *   "Tyler@Example.com"  -> "t***@example.com"
 *   "a@b.io"             -> "a***@b.io"
 *   "not-an-email"       -> "not-an-email"  (no '@' → returned as-is)
 *
 * For emails where the local part is a single character, the output is still
 * `<char>***@<domain>` (e.g. "a@b.io" → "a***@b.io").
 */
export function maskEmail(email: string): string {
  const lower = email.toLowerCase().trim();
  const atIndex = lower.indexOf('@');
  if (atIndex <= 0) return lower;
  const local = lower.slice(0, atIndex);
  const domain = lower.slice(atIndex); // includes '@'
  return local[0] + '***' + domain;
}

/**
 * Compare two masked email strings case-insensitively.
 *
 * Returns false if either argument is null, undefined, or an empty string —
 * these represent "unknown" states where we cannot confirm a match.
 */
export function masksMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}
